import { Grade, useGenerateCompetencyMapByDocumentsMutation } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Box, Card, Stack, Typography } from "@mui/material";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { getWordDeclension } from "helpers/index";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { QUESTION_TYPE_ERROR_MESSAGE } from "../../constants";
import { CompetencyMapGenerationFormValues, GenerationQuestionMetrics, PromptSlideId } from "../../types";
import { buildTonePrompt, calculateQuestionMetrics, getQuestionTypeConstraints } from "../../utils";

interface InfoBlockProps {
  questionMetrics: GenerationQuestionMetrics;
  difficultyGradeLabel: Grade;
  skippedPromptIds: PromptSlideId[];
}

const getTokenLabel = (tokens: number) => {
  if (tokens === 1) {
    return "токен";
  }
  if (tokens >= 5) {
    return "токенов";
  }

  return "токена";
};

const InfoBlock = ({ questionMetrics, difficultyGradeLabel, skippedPromptIds }: InfoBlockProps) => {
  const [generateCompetencyMapByDocuments, { loading }] = useGenerateCompetencyMapByDocumentsMutation();

  const form = useFormContext<CompetencyMapGenerationFormValues>();
  const { handleSubmit, watch, setError } = form;
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const files = watch("files") || [];
  const difficulty = watch("difficulty") || 5;

  const onSubmit = async (formData: CompetencyMapGenerationFormValues) => {
    const questionTypeConstraints = getQuestionTypeConstraints(formData.questionTypeCounts);

    if (!questionTypeConstraints.length) {
      setError("questionTypeCounts", { type: "manual", message: QUESTION_TYPE_ERROR_MESSAGE });
      return;
    }

    const recalculatedMetrics = calculateQuestionMetrics(formData.questionTypeCounts, formData.difficulty);
    const criteriaPrompt = skippedPromptIds.includes("criteriaPrompt")
      ? undefined
      : formData.criteriaPrompt.trim() || undefined;
    const globalPrompt = skippedPromptIds.includes("globalPrompt")
      ? undefined
      : formData.globalPrompt.trim() || undefined;
    const questionsPrompt = skippedPromptIds.includes("questionsPrompt")
      ? undefined
      : formData.questionsPrompt.trim() || undefined;
    const tonePrompt = skippedPromptIds.includes("tonePrompt")
      ? undefined
      : buildTonePrompt(formData.tonePrompt, formData.toneContext) || undefined;

    await generateCompetencyMapByDocuments({
      variables: {
        input: {
          title: formData.title.trim(),
          specialization: formData.specialization.trim(),
          files: formData.files,
          difficulty: formData.difficulty,
          durationMinutes: recalculatedMetrics.estimatedDuration,
          language: formData.language,
          questionTypeConstraints,
          globalPrompt,
          questionsPrompt,
          criteriaPrompt,
          tonePrompt,
        },
      },
    })
      .then((res) => {
        const competencyMapId = res.data?.generateCompetencyMapByDocuments.id;

        if (!competencyMapId) {
          addToast({ type: ToastTypeEnum.ERROR });
          return;
        }

        addToast({
          type: ToastTypeEnum.SUCCESS,
          text: "Запрос на генерацию карты компетенций отправлен",
        });
        navigate(`/${ROUTES.COMPETENCE_MAPS}/view/${competencyMapId}`);
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  return (
    <Stack
      spacing={1}
      sx={{ height: "100%" }}>
      <Card sx={{ p: 1.5 }}>
        <Typography
          variant='body16Extra'
          color='text.secondary'>
          Типов включено
        </Typography>
        <Stack
          direction='row'
          alignItems='baseline'
          spacing={0.5}>
          <Typography variant='h4'>{questionMetrics.totalActiveTypes}</Typography>
          <Typography
            variant='subtitle2'
            color='text.secondary'>
            {getWordDeclension(questionMetrics.totalActiveTypes, "type")}
          </Typography>
        </Stack>
      </Card>
      <Card sx={{ p: 1.5 }}>
        <Typography
          variant='body16Extra'
          color='text.secondary'>
          Расч. время
        </Typography>
        <Stack
          direction='row'
          alignItems='baseline'
          spacing={0.5}>
          <Typography variant='h4'>~{questionMetrics.estimatedDuration}</Typography>
          <Typography
            variant='subtitle2'
            color='text.secondary'>
            мин.
          </Typography>
        </Stack>
        <Typography color='text.secondary'>
          {questionMetrics.totalQuestions} вопр. · {questionMetrics.tokens} {getTokenLabel(questionMetrics.tokens)}
        </Typography>
      </Card>
      <Card sx={{ p: 1.5 }}>
        <Typography
          variant='body16Extra'
          color='text.secondary'>
          Грейд
        </Typography>
        <Typography
          variant='h5'
          fontWeight={600}>
          {difficultyGradeLabel}
        </Typography>
        <Typography color='text.secondary'>сложность {difficulty}/10</Typography>
      </Card>
      <Card
        sx={{
          p: 1.5,
          flex: 1,
          minHeight: 0,
          "& > *": {
            height: "100%",
          },
        }}>
        <Stack spacing={1}>
          <Typography
            variant='body16Extra'
            color='text.secondary'>
            Микс
          </Typography>
          <Stack spacing={0.75}>
            {questionMetrics.mix.map((item) => (
              <Box key={item.id}>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  sx={{ mb: 0.25 }}>
                  <Typography variant='body2'>{item.label}</Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'>
                    {item.percent}%
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
          {!questionMetrics.mix.length && <Typography color='text.secondary'>Типы вопросов не выбраны.</Typography>}
        </Stack>
      </Card>
      <LoadingButton
        variant='contained'
        size='large'
        loading={loading}
        disabled={!files.length}
        onClick={handleSubmit(onSubmit)}
        sx={{ py: 1.5 }}>
        Сгенерировать
      </LoadingButton>
    </Stack>
  );
};

export default InfoBlock;
